import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { IRetryPolicy } from '@microsoft/signalr';
import { ConfigService } from '@stockcare-services/config-service/config.service';
import { ConnectionService } from '@stockcare-services/connection-service/connection.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { PollingServicesService } from '@stockcare-services/polling-services/polling-services.service';
import { TheatreRefreshService } from '@stockcare-services/theatre-patient-service/theatre-refresh.service';
import { Subscription } from 'rxjs';

export class SignalRRetryPolicy implements IRetryPolicy {
  nextRetryDelayInMilliseconds(retryContext: signalR.RetryContext): number {
    return 5000;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private hubConnection: signalR.HubConnection;
  subscriptions = new Subscription();

  constructor(
    private deviceService: DeviceService,
    private config: ConfigService,
    private readonly theatreRefreshService: TheatreRefreshService,
    private pollingServicesService: PollingServicesService,
    private connectionService: ConnectionService
  ) {
    this.subscribeToDeviceUpdated();
  }

  public startConnection = () => {
    this.connectionService.setOffline();
    if (this.deviceService.isDeviceLinked()) {
      var deviceId = this.deviceService.getDeviceId();

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.signalRUrl}/signalr?deviceId=${deviceId}`, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(new SignalRRetryPolicy())
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.retryConnection();

      this.hubConnection.on('BroadcastAll', (data: string) => {
        console.log('broadcast message received. Data:' + data);
      });
      this.hubConnection.on('PatientMoved', () => {
        this.theatreRefreshService.publishUpdate();
      });
      this.hubConnection.on('BroadcastHealthCheck', (correlationId: string) => {
        const healthInformation = this.pollingServicesService.getHealthInformation();
        this.deviceService.sendHealth(correlationId, healthInformation).subscribe();
      });

      this.hubConnection.onreconnecting((error) => {
        this.connectionService.setOffline();
        console.log(error);
      });
      this.hubConnection.onreconnected(() => {
        this.connectionService.setOnline();
        console.log('web socket reconnected.');
      });
    }
  };

  private retryConnection() {
    this.hubConnection
      .start()
      .then(() => {
        this.connectionService.setOnline();
        console.log('Web socket connection started');
      })
      .catch((err) => {
        console.log('Error while establishing signalR connection... Retrying...');
        this.connectionService.setOffline();
        setTimeout(() => this.retryConnection(), 3000);
      });
  }

  get signalRUrl() {
    let baseUrl = this.config.config.baseUrl;

    return baseUrl.replace('/api', '');
  }

  private subscribeToDeviceUpdated() {
    this.subscriptions.add(
      this.deviceService.localDeviceUpdated.subscribe(() => {
        this.hubConnection?.stop();
        this.startConnection();
      })
    );
  }

  public ensureConnected() {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      this.hubConnection.stop();
      this.startConnection();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
